import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, Search, MapPin, BedDouble, Bath, Maximize2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { publicLeadsService } from '../services/api';
import { API_BASE } from '@/lib/env';
import { resolveImageUrl } from '@/lib/utils';
import PropertyGallery from '../components/PropertyGallery';

interface Property {
  id: string;
  title: string;
  price: number;
  bedrooms: number | null;
  bathrooms: number | null;
  area: number | null;
  address: string | null;
  property_type: string;
  description: string | null;
  images?: string[];
}

interface LeadFormState {
  name: string;
  email: string;
  phone: string;
  submitting: boolean;
  submitted: boolean;
  error: string;
}

const INITIAL_LEAD: LeadFormState = { name: '', email: '', phone: '', submitting: false, submitted: false, error: '' };

function PropertyDetail({ property, agencyId, onClose }: { property: Property; agencyId: string; onClose: () => void }) {
  const [lead, setLead] = useState<LeadFormState>(INITIAL_LEAD);

  const submitLead = async (e: FormEvent) => {
    e.preventDefault();
    setLead((p) => ({ ...p, submitting: true, error: '' }));
    try {
      await publicLeadsService.create({
        agency_id: agencyId,
        source: 'search',
        source_property_id: property.id,
        name: lead.name || null,
        email: lead.email || null,
        phone: lead.phone || null,
      });
      setLead((p) => ({ ...p, submitted: true, submitting: false }));
    } catch {
      setLead((p) => ({ ...p, error: 'Error al enviar. Intenta de nuevo.', submitting: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-semibold truncate pr-4">{property.title}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {property.images && property.images.length > 0 && (
            <PropertyGallery images={property.images} />
          )}
          <p className="text-2xl font-semibold text-primary">${property.price.toLocaleString('es-MX')}</p>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {property.address && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{property.address}</span>}
            {property.bedrooms != null && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{property.bedrooms} rec.</span>}
            {property.bathrooms != null && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{property.bathrooms} baños</span>}
            {property.area != null && <span className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" />{property.area} m²</span>}
          </div>
          {property.description && <p className="text-sm text-muted-foreground leading-relaxed">{property.description}</p>}

          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-3">¿Te interesa? Deja tus datos</p>
            {lead.submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700">
                Gracias. Un asesor te contactará pronto.
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-name">Nombre</Label>
                  <Input id="lead-name" value={lead.name} onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))} placeholder="Tu nombre" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-email">Email</Label>
                  <Input id="lead-email" type="email" value={lead.email} onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))} placeholder="tu@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-phone">Teléfono</Label>
                  <Input id="lead-phone" value={lead.phone} onChange={(e) => setLead((p) => ({ ...p, phone: e.target.value }))} placeholder="+52 55 0000 0000" />
                </div>
                {lead.error && <p className="text-xs text-destructive">{lead.error}</p>}
                <Button type="submit" className="w-full active:scale-[0.98] transition-all" disabled={lead.submitting}>
                  {lead.submitting ? 'Enviando...' : 'Contactar asesor'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PropertyCardSkeleton() {
  return (
    <div className="border border-border rounded-xl animate-pulse">
      <div className="h-1 rounded-t-xl bg-muted" />
      <div className="p-5 space-y-2">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="h-6 bg-muted rounded w-1/3 mt-2" />
      </div>
    </div>
  );
}

function BuscarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? localStorage.getItem('buscar_filter_q') ?? '');
  const [priceMax, setPriceMax] = useState(searchParams.get('price_max') ?? localStorage.getItem('buscar_filter_priceMax') ?? '');
  const [bedrooms, setBedrooms] = useState(searchParams.get('bedrooms') ?? localStorage.getItem('buscar_filter_bedrooms') ?? '');
  const [results, setResults] = useState<Property[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState<Property | null>(null);
  const agencyId = searchParams.get('agency') ?? '';
  const [agencyName, setAgencyName] = useState<string>('');

  useEffect(() => { document.title = 'Buscar propiedades — Takarah'; }, []);

  useEffect(() => {
    if (!agencyId) return;
    axios.get(`${API_BASE}/public/agencies/${agencyId}`)
      .then((r) => setAgencyName(r.data.name))
      .catch(() => {}); // non-fatal
  }, [agencyId]);

  const doSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!agencyId) {
      setSearched(true);
      setResults([]);
      return;
    }
    setLoading(true);
    setSearched(true);
    setSearchError(false);
    if (query) localStorage.setItem('buscar_filter_q', query); else localStorage.removeItem('buscar_filter_q');
    if (priceMax) localStorage.setItem('buscar_filter_priceMax', priceMax); else localStorage.removeItem('buscar_filter_priceMax');
    if (bedrooms) localStorage.setItem('buscar_filter_bedrooms', bedrooms); else localStorage.removeItem('buscar_filter_bedrooms');
    const newParams: Record<string, string> = {};
    if (query) newParams.q = query;
    if (priceMax) newParams.price_max = priceMax;
    if (bedrooms) newParams.bedrooms = bedrooms;
    newParams.agency = agencyId;
    setSearchParams(newParams);
    try {
      const res = await axios.post(`${API_BASE}/public/properties/search`, {
        agency_id: agencyId,
        q: query || undefined,
        price_max: priceMax ? Number(priceMax) : undefined,
        bedrooms: bedrooms ? Number(bedrooms) : undefined,
      });
      setResults(res.data?.items ?? []);
    } catch {
      setResults([]);
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search on mount if URL already carries ?agency=X
  useEffect(() => {
    if (agencyId) doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary shrink-0" />
          <span className="font-semibold tracking-tight">{agencyName || 'Takarah'}</span>
          <span className="text-muted-foreground text-xs ml-1 hidden sm:block">· Buscador de propiedades</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight mb-1">Encuentra tu propiedad</h1>
          <p className="text-muted-foreground text-sm">Busca entre el catálogo disponible</p>
        </div>

        {/* Search form */}
        <form onSubmit={doSearch} className="space-y-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Busca por nombre, colonia, tipo…"
              className="pl-9"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((p) => !p)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showFilters ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showFilters ? 'Ocultar filtros' : 'Más filtros'}
          </button>

          {showFilters && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="price-max">Precio máximo</Label>
                <Input
                  id="price-max"
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="5 000 000"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bedrooms">Recámaras mín.</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  placeholder="2"
                />
              </div>
            </div>
          )}

          <Button type="submit" className="w-full active:scale-[0.98] transition-all" disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </form>

        {/* Results */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <PropertyCardSkeleton key={i} />)}
          </div>
        )}

        {!agencyId && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="font-medium">Enlace inválido</p>
            <p className="text-sm text-muted-foreground mt-1">Este buscador requiere un enlace de agencia válido.</p>
          </div>
        )}

        {agencyId && !loading && searchError && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="font-medium">Error al buscar</p>
            <p className="text-sm text-muted-foreground mt-1">Intenta de nuevo en unos momentos.</p>
          </div>
        )}

        {agencyId && !loading && searched && !searchError && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="font-medium">Sin resultados</p>
            <p className="text-sm text-muted-foreground mt-1">Prueba con otros filtros o una búsqueda diferente.</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className="text-left border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-sm transition-all active:scale-[0.99] group"
              >
                {p.images && p.images.length > 0 ? (
                  <img
                    src={resolveImageUrl(p.images[0])}
                    alt={p.title}
                    loading="lazy"
                    className="w-full aspect-[16/9] object-cover bg-muted"
                  />
                ) : (
                  <div className="h-1 bg-primary/40 group-hover:bg-primary transition-colors" />
                )}
                <div className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{p.property_type}</p>
                  <h3 className="font-semibold line-clamp-2 leading-tight">{p.title}</h3>
                  {p.address && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3" />{p.address}
                    </p>
                  )}
                  <p className="text-xl font-semibold text-primary mt-3">${p.price.toLocaleString('es-MX')}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    {p.bedrooms != null && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{p.bedrooms}</span>}
                    {p.bathrooms != null && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.bathrooms}</span>}
                    {p.area != null && <span className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" />{p.area} m²</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-border mt-12 py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="w-3.5 h-3.5" />
          <span>Takarah · Buscador público</span>
        </div>
      </footer>

      {selected && (
        <PropertyDetail
          property={selected}
          agencyId={agencyId}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

export default BuscarPage;
